import { DropdownOption } from "@modernatx/ui-kit-react-v2";
import { isUndefined } from "lodash";
import Router, { useRouter } from "next/router";
import React from "react";

import { FinderBlockProps } from "@/components/V2/Block";
import { useExperience } from "@/context/ExperienceContext";
import { useTrackAppState } from "@/context/TrackAppStateContext";
import { FinderLocation } from "@/types/FinderLocation";

import { useMapContext } from "../googleMaps/GoogleMapsProvider";
import { getFinderCountryConfig } from "../utils";
import fetchLocations from "../utils/fetchLocations";
import getPlaceFromId from "../utils/getPlaceFromId";
import getPlaceFromReverseGeocode from "../utils/getPlaceFromReverseGeocode";
import { useEligibility } from "./useEligibility";
import { LocationDropdownOption } from "./useLocationSearchV2";
import { useVisibleSearchResults } from "./useVisibleSearchResults";

type SearchType = "unbranded" | "branded";

export interface Place {
  address: string;
  id: string;
  latitude: number;
  longitude: number;
}

interface FinderContextValue {
  age?: number;
  ageSet: React.Dispatch<React.SetStateAction<number | undefined>>;
  allSearchResults: FinderLocation[];
  couldSearchExceptForAge: boolean;
  handleTypeSelect: (option: DropdownOption) => void;
  locationSearchOption: LocationDropdownOption | null;
  locationSearchOptionSet: (option: LocationDropdownOption) => void;
  locationSelected: FinderLocation | null;
  locationSelectedSet: (location: FinderLocation) => void;
  mapBasedSearch: (searchMarker: HTMLDivElement | null, isMobileView: boolean) => void;
  noResultsFound: boolean;
  place: Place | null;
  searchId?: string;
  searching: boolean;
  searchType: SearchType;
  searchValue: string;
  searchValueSet: React.Dispatch<React.SetStateAction<string>>;
  selectedVaccineTypes: string[];
  selectedVaccineTypesSet: React.Dispatch<React.SetStateAction<string[]>>;
  setNumExtraVisibleResults: React.Dispatch<React.SetStateAction<number>>;
  showSearchLocationMarker: boolean;
  vaccineTypesFromQuery: string[];
  visibleSearchResults: FinderLocation[];
}

const FinderContext = React.createContext<FinderContextValue>({
  ageSet: () => {},
  allSearchResults: [],
  couldSearchExceptForAge: false,
  handleTypeSelect: () => {},
  locationSearchOption: null,
  locationSearchOptionSet: () => {},
  locationSelected: null,
  locationSelectedSet: () => {},
  mapBasedSearch: () => {},
  noResultsFound: false,
  place: null,
  searching: false,
  searchType: "branded",
  searchValue: "",
  searchValueSet: () => {},
  selectedVaccineTypes: [],
  selectedVaccineTypesSet: () => {},
  setNumExtraVisibleResults: () => {},
  showSearchLocationMarker: false,
  vaccineTypesFromQuery: [],
  visibleSearchResults: []
});

const productsFromVaccineTypes = (
  vaccineTypes: string[],
  ageEligibility: FinderBlockProps["search"]["eligibility"]["key"]
) => {
  const products = vaccineTypes.reduce((acc: string[], type) => {
    const productsForType = ageEligibility.products[type];
    if (productsForType) {
      acc.push(...productsForType);
    }
    return acc;
  }, []);

  const uniqueProducts = Array.from(new Set(products));

  return uniqueProducts;
};

const updateUrlWithQueryParams = (vaccineTypesString: string, placeId: string, age: number) => {
  const productsQuery = vaccineTypesString.length ? { vaccineType: vaccineTypesString } : {};

  return new Promise<void>((resolve) => {
    Router.push(
      {
        pathname: location.pathname,
        query: {
          ...Router.query,
          placeId: placeId,
          age,
          ...productsQuery
        }
      },
      undefined,
      { scroll: false }
    ).then(() => resolve());
  });
};

interface FinderResults {
  allSearchResults: FinderLocation[];
  productsSelected: string[];
}

export const FinderContextProvider: React.FC<
  React.PropsWithChildren<{
    vaccineTypeOptions: string[];
    searchType: SearchType;
    v2Search?: boolean;
    eligibility?: FinderBlockProps["search"]["eligibility"];
  }>
> = ({ children, searchType, v2Search = false, eligibility, vaccineTypeOptions }) => {
  const { country, language } = useExperience();
  const { query } = useRouter();
  const { setHasPanned } = useMapContext();
  const [locationSelected, locationSelectedSet] = React.useState<FinderLocation | null>(null);
  const [place, placeSet] = React.useState<null | Place>(null);
  const [searching, searchingSet] = React.useState(false);
  const [locationSearchOption, locationSearchOptionSet] =
    React.useState<LocationDropdownOption | null>(null);
  const [searchValue, searchValueSet] = React.useState("");
  const [age, ageSet] = React.useState<number>();
  const [selectedVaccineTypes, selectedVaccineTypesSet] = React.useState<string[]>([]);
  const [numExtraVisibleResults, setNumExtraVisibleResults] = React.useState(0);
  const [finderResults, finderResultsSet] = React.useState<FinderResults>({
    allSearchResults: [],
    productsSelected: []
  });
  const initialQuery = React.useRef<string | undefined>();
  const searchId = React.useRef<string | undefined>();
  const hasRunFirstSearch = React.useRef(false);

  const { size } = React.useMemo(() => getFinderCountryConfig(country), [country]);
  const eligibilityForAge = useEligibility(eligibility, age);
  const canSearch = !!(place?.latitude && place?.longitude && place.id && !isUndefined(age));
  const couldSearchExceptForAge = !!(
    place?.latitude &&
    place?.longitude &&
    place.id &&
    isUndefined(age)
  );

  const setPlaceFromPlaceId = React.useCallback(
    async (placeId: string) => {
      if (placeId && country && language) {
        let newPlace;
        if (placeId.startsWith("map_")) {
          newPlace = getPlaceFromReverseGeocode(placeId);
        } else {
          newPlace = await getPlaceFromId(placeId, country, language);
        }
        if (newPlace) {
          placeSet(newPlace);
          return newPlace;
        }
        return;
      }
    },
    [country, language]
  );

  // Pull query params from URL
  React.useEffect(() => {
    if (initialQuery.current !== JSON.stringify(query) && country && language) {
      initialQuery.current = JSON.stringify(query);
      const queryPlaceId = Array.isArray(query.placeId) ? query.placeId[0] : query.placeId;
      const queryAge = Array.isArray(query.age) ? query.age[0] : query.age;
      const queryVaccineTypes = Array.isArray(query.vaccineType)
        ? query.vaccineType
        : query.vaccineType?.split(",");

      const parsedAge = parseInt(queryAge || "", 10);
      if (!isNaN(parsedAge)) {
        ageSet(parsedAge);
      }
      if (queryVaccineTypes) {
        selectedVaccineTypesSet(queryVaccineTypes);
      } else {
        selectedVaccineTypesSet(vaccineTypeOptions);
      }
      if (queryPlaceId) {
        setPlaceFromPlaceId(queryPlaceId).then((newPlace) => {
          if (newPlace) {
            locationSearchOptionSet({ label: newPlace.address, value: newPlace.id });
          }
        });
      }
    }
  }, [query, initialQuery, country, language, setPlaceFromPlaceId, vaccineTypeOptions]);

  const vaccineTypesString = selectedVaccineTypes.join(",");

  const productsForSearch = React.useMemo(() => {
    if (!eligibilityForAge) {
      return "";
    }

    return productsFromVaccineTypes(selectedVaccineTypes, eligibilityForAge).join(",");
  }, [selectedVaccineTypes, eligibilityForAge]);

  // THE callback to run a search and update locations
  const runSearch = React.useCallback(() => {
    if (canSearch) {
      searchingSet(true);
      hasRunFirstSearch.current = true;

      const searchParams = new URLSearchParams({
        country: country as string,
        lat: String(place.latitude),
        lon: String(place.longitude),
        products: productsForSearch,
        type: searchType
      });

      const getLocations = async () => {
        let results: FinderLocation[] = [];
        try {
          const resolvedLocations: FinderLocation[] = await fetchLocations(searchParams).catch(
            () => {
              return [];
            }
          );

          // Sort locations by distance and return the first "size" locations - where size is the total number of locations we can display.
          // This is necessary because in the unbranded finder, we fetch from our datastore up to 30 results, and then also we fetch from walgreens API
          // sometimes when we combine the two, we get more results than we can show.
          const sortedAndSlicedResolvedLocations = resolvedLocations
            .sort((a, b) => a.location.distance - b.location.distance)
            .slice(0, size);

          await updateUrlWithQueryParams(vaccineTypesString, place.id, age);

          results = sortedAndSlicedResolvedLocations;
          // allSearchResultsSet(sortedAndSlicedResolvedLocations);
        } finally {
          searchId.current = searchParams.toString();
          setHasPanned(false); // Reset the panning state
          searchingSet(false); // Ensure the searching state is reset
          locationSelectedSet(null); // Reset the selected location
          setNumExtraVisibleResults(0); // Reset the number of extra visible results
          finderResultsSet({
            allSearchResults: results,
            productsSelected: productsForSearch.split(",")
          });
        }
      };

      getLocations();
    } else {
      searchingSet(false);
    }
  }, [
    age,
    canSearch,
    country,
    place?.id,
    place?.latitude,
    place?.longitude,
    productsForSearch,
    searchType,
    setHasPanned,
    size,
    vaccineTypesString
  ]);

  const handleTypeSelect = (option: DropdownOption) => {
    const type = option.value as string;

    if (selectedVaccineTypes.includes(type)) {
      selectedVaccineTypesSet((prev) => prev.filter((t) => t !== type));
    } else {
      selectedVaccineTypesSet((prev) => [...prev, type]);
    }
  };

  // Set Place and Search Value when Location Search Option changes
  React.useEffect(() => {
    if (v2Search && locationSearchOption?.label && locationSearchOption?.value) {
      searchValueSet(locationSearchOption.label);
      setPlaceFromPlaceId(locationSearchOption.value as string);
    }
  }, [v2Search, setPlaceFromPlaceId, locationSearchOption?.label, locationSearchOption?.value]);

  // Run new search when filter state changes
  React.useEffect(() => {
    const handler = setTimeout(() => {
      if (v2Search) {
        if (canSearch) {
          runSearch();
        }
      }
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [v2Search, runSearch, canSearch]);

  const noResultsFound = React.useMemo(() => {
    if (searching || !searchId.current) {
      return false;
    }

    return finderResults.allSearchResults.length === 0;
  }, [searching, finderResults.allSearchResults]);

  const vaccineTypesFromQuery = React.useMemo(() => {
    const types = finderResults.productsSelected.reduce((acc: string[], product) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const found = Object.entries(eligibilityForAge?.products || {}).find(([_key, value]) => {
        if (value.includes(product)) {
          return true;
        }

        return false;
      });

      if (found) {
        acc.push(found[0]);
      }

      return acc;
    }, []);
    return Array.from(new Set(types));
  }, [finderResults.productsSelected, eligibilityForAge]);

  const { visibleSearchResults, showSearchLocationMarker, hasVisibleExpandedResults } =
    useVisibleSearchResults({ ...finderResults, numExtraVisibleResults });

  // Update App State when finder state changes
  useTrackAppState({
    "num-results-total": finderResults.allSearchResults.length,
    "num-results-displayed": visibleSearchResults.length,
    "app-state": { "visible-expanded-results": hasVisibleExpandedResults }
  });

  return (
    <FinderContext.Provider
      value={{
        age,
        ageSet,
        allSearchResults: finderResults.allSearchResults,
        couldSearchExceptForAge,
        handleTypeSelect,
        locationSearchOption,
        locationSearchOptionSet,
        locationSelected,
        locationSelectedSet,
        mapBasedSearch: () => {},
        place,
        searching,
        searchType,
        searchValue,
        searchValueSet,
        selectedVaccineTypes,
        selectedVaccineTypesSet,
        setNumExtraVisibleResults,
        showSearchLocationMarker,
        vaccineTypesFromQuery,
        visibleSearchResults,
        noResultsFound,
        searchId: searchId.current
      }}
    >
      {children}
    </FinderContext.Provider>
  );
};

export const useFinder = () => React.useContext(FinderContext);
